import * as turf from '@turf/turf';

import { MapSource } from "./MapSource";
import { MapLayers } from "./MapLayers";
import { MapMarkers } from "./MapMarkers";

class MapState {
    constructor() {
      this.indexedFields = new Map();
      this.indexedLands = new Map();
      this.globalState = { ok: false, value: null }; // State to keep track of current drawing status
  
      this.landsActive = []; // List of lands with active layers
      this.fieldsActive = []; // List of fields with active layers

      // map instance
      this.map = null;

      // map source;
      this._source = null;

      // map layers
      this._layers = null;

      // map markers
      this._markers = null;
    }
  
    setMap(map) {
        this.map = map;
    }

    isInitialized() {
        return this.map != null && this._source != null && this._source.isInitialized();
    }

    source() {
        if (this._source == null) {
            this._source = new MapSource(this.map);
        }

        return this._source;
    }

    layers() {
        if (this._layers == null) {
            this._layers = new MapLayers(this.map, this);
        }

        return this._layers;
    }

    markers() {
        if (this._markers == null) {
            this._markers = new MapMarkers(this.map, this);
        }

        return this._markers;
    }

    fit(polygon) {
        this.map.fitBounds(turf.bbox(polygon), getMapFitBoundsOptions());
    }

    reload(){
        this._source = null;
        this._layers = null;
        this._markers = null;
        this.map = null;

    }
}
  

const getDefaultFitPadding = () => ({
    top: 48,
    right: 24,
    bottom: 48,
    left: 24
});

const getDesktopPanelRect = () => {
    const panel = document.querySelector('.right-gis-container');

    if (!(panel instanceof HTMLElement)) {
        return null;
    }

    const rect = panel.getBoundingClientRect();

    if (rect.width < 280 || rect.height < 160) {
        return null;
    }

    return rect;
};

const getHeaderBottom = () => {
    const header = document.querySelector('#kt_header, .app-header, .header');
    return header instanceof HTMLElement ? header.getBoundingClientRect().bottom : 72;
};

const getMapContainerRect = () => {
    const container = mapState?.map?.getContainer?.() || document.querySelector('.map-container');

    if (!(container instanceof HTMLElement)) {
        return null;
    }

    return container.getBoundingClientRect();
};

export const getMapFitBoundsOptions = () => {
    const defaultPadding = getDefaultFitPadding();

    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return { padding: defaultPadding };
    }

    if (window.innerWidth > 700) {
        const desktopPanelRect = getDesktopPanelRect();

        if (desktopPanelRect == null) {
            return { padding: defaultPadding };
        }

        return {
            padding: {
                top: defaultPadding.top,
                right: Math.max(defaultPadding.right, Math.ceil(window.innerWidth - desktopPanelRect.left + 24)),
                bottom: defaultPadding.bottom,
                left: defaultPadding.left
            }
        };
    }

    const searchControl = document.querySelector('.mapboxgl-ctrl-top-left');
    const drawer = document.getElementById('kt_right_container');
    const legend = document.getElementById('kt_right_mobile_legend');
    const hasLegend = legend instanceof HTMLElement;
    const mapRect = getMapContainerRect();
    const searchBottom = searchControl instanceof HTMLElement
        ? searchControl.getBoundingClientRect().bottom
        : getHeaderBottom() + 24;
    const drawerTop = drawer instanceof HTMLElement
        ? drawer.getBoundingClientRect().top
        : window.innerHeight;
    const legendTop = legend instanceof HTMLElement
        ? legend.getBoundingClientRect().top
        : window.innerHeight;
    const lowerBoundaryTop = Math.min(drawerTop, legendTop);
    const mapTop = mapRect?.top ?? 0;
    const mapBottom = mapRect?.bottom ?? window.innerHeight;
    const topPadding = Math.max(defaultPadding.top, Math.ceil(searchBottom - mapTop + 10));
    const bottomPadding = Math.max(defaultPadding.bottom, Math.ceil(mapBottom - lowerBoundaryTop + (hasLegend ? 20 : 16)));

    return {
        padding: {
            top: topPadding,
            right: hasLegend ? 16 : 20,
            bottom: bottomPadding,
            left: hasLegend ? 16 : 20
        }
    };
};

export const mapState = new MapState();
